import { useState, useCallback, useEffect } from 'react';
import { useGetFooterConfig, useUpdateFooterConfig } from '../api/layout/useFooterConfigApi';

const DEFAULT_NAVIGATION_SECTIONS = [
  {
    key: 'customer',
    title: 'Help',
    links: [
      { label: 'Customer Service', url: '/customer-service', active: true },
      { label: 'FAQ', url: '/faq', active: true },
      { label: 'Manage Booking', url: '/manage-booking', active: true },
      { label: 'Cancellation Policy', url: '/cancellation-policy', active: true },
      { label: 'Travel Insurance', url: '/travel-insurance', active: true },
      { label: 'Contact Us', url: '/contact-us', active: true },
    ],
  },
  {
    key: 'content',
    title: 'Inspiration',
    links: [
      { label: 'Blog', url: '/blog', active: true },
      { label: 'Top Destinations', url: '/top-destinations', active: true },
      { label: 'Travel Guides', url: '/travel-guides', active: true },
      { label: 'Best Deals', url: '/best-deals', active: true },
      { label: 'Seasonal Picks', url: '/seasonal-picks', active: true },
      { label: 'Newsletter', url: '/newsletter', active: true },
    ],
  },
  {
    key: 'company',
    title: 'About',
    links: [
      { label: 'About Us', url: '/about-us', active: true },
      { label: 'Careers', url: '/careers', active: true },
      { label: 'Press', url: '/press', active: true },
      { label: 'Partners', url: '/partners', active: true },
      { label: 'Sustainability', url: '/sustainability', active: true },
      { label: 'Terms', url: '/terms', active: true },
    ],
  },
];

const cloneDefaultSections = () => DEFAULT_NAVIGATION_SECTIONS.map((section) => ({
  ...section,
  links: section.links.map((link) => ({ ...link })),
}));

const slugify = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const toStringSafe = (value) => (value == null ? '' : String(value));

function normalizeNavigationSections(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((section, index) => {
      const title = (section?.title || section?.label || '').toString();
      const key = slugify(section?.key || title || `section-${index + 1}`) || `section-${index + 1}`;
      const links = Array.isArray(section?.links)
        ? section.links.map((link) => ({
            label: link?.label ?? '',
            url: link?.url ?? '/',
            active: link?.active !== false,
          }))
        : [];
      return { key, title: title || `Section ${index + 1}`, links };
    })
    .filter((section) => section.title || section.links.length > 0);
}

function sectionsFromLegacyNavigation(navigation) {
  if (!navigation || typeof navigation !== 'object') return [];
  return Object.entries(navigation).map(([key, links], index) => ({
    key: slugify(key) || `section-${index + 1}`,
    title: key
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase()),
    links: Array.isArray(links)
      ? links.map((link) => ({
          label: link?.label ?? '',
          url: link?.url ?? '/',
          active: link?.active !== false,
        }))
      : [],
  }));
}

function toNavigationObject(sections = []) {
  return sections.reduce((acc, section) => {
    acc[section.key] = section.links || [];
    return acc;
  }, {});
}

const defaultConfig = {
  brandName: 'Sunsky',
  brandDescription:
    "Your gateway to the world's most beautiful destinations. Premium holidays at transparent prices, backed by award-winning service.",
  brandLogoUrl: '',
  navigationSections: cloneDefaultSections(),
  paymentIcons: [
    { label: 'VISA', active: true },
    { label: 'MC', active: true },
    { label: 'AMEX', active: true },
    { label: 'iDEAL', active: true },
    { label: 'PP', active: true },
  ],
  copyrightText: '\u00A9 2026 Sunsky Travel. All rights reserved.',
};

function mapApiToLocal(apiConfig) {
  if (!apiConfig) {
    const sections = cloneDefaultSections();
    return {
      ...defaultConfig,
      navigationSections: sections,
    };
  }

  const sections =
    normalizeNavigationSections(apiConfig.navigationSections).length > 0
      ? normalizeNavigationSections(apiConfig.navigationSections)
      : sectionsFromLegacyNavigation(apiConfig.navigation).length > 0
        ? sectionsFromLegacyNavigation(apiConfig.navigation)
        : cloneDefaultSections();

  return {
    brandName: apiConfig.brandName ?? defaultConfig.brandName,
    brandDescription: apiConfig.brandDescription ?? defaultConfig.brandDescription,
    brandLogoUrl: apiConfig.brandLogoUrl ?? defaultConfig.brandLogoUrl,
    navigationSections: sections,
    paymentIcons: (apiConfig.paymentIcons || []).map((item) => ({
      label: item.label ?? '',
      active: item.active !== false,
      imageUrl: item.imageUrl,
    })),
    copyrightText: apiConfig.copyrightText ?? defaultConfig.copyrightText,
  };
}

function buildUpdatePayload(local) {
  const sections = normalizeNavigationSections(local.navigationSections).map((section, index) => ({
    key: slugify(section.key || section.title || `section-${index + 1}`) || `section-${index + 1}`,
    title: toStringSafe(section.title).trim(),
    links: (Array.isArray(section.links) ? section.links : [])
      .map((link) => ({
        label: toStringSafe(link?.label).trim(),
        url: toStringSafe(link?.url).trim() || '/',
        active: link?.active !== false,
      }))
      .filter((link) => link.label || link.url !== '/'),
  }));

  const paymentIcons = (Array.isArray(local.paymentIcons) ? local.paymentIcons : []).map(
    ({ label, active, imageUrl }) => ({
      label: toStringSafe(label).trim(),
      active: active !== false,
      imageUrl: toStringSafe(imageUrl).trim() || null,
    })
  );

  return {
    brandName: toStringSafe(local.brandName).trim(),
    brandDescription: toStringSafe(local.brandDescription).trim(),
    brandLogoUrl: toStringSafe(local.brandLogoUrl).trim(),
    navigationSections: sections,
    navigation: toNavigationObject(sections),
    paymentIcons,
    copyrightText: toStringSafe(local.copyrightText).trim(),
  };
}

export function useFooterConfig() {
  const [config, setConfig] = useState(() => {
    const sections = cloneDefaultSections();
    return {
      ...defaultConfig,
      navigationSections: sections,
    };
  });
  const [lastSaved, setLastSaved] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const getApi = useGetFooterConfig();
  const updateApi = useUpdateFooterConfig();

  const load = useCallback(async () => {
    try {
      const res = await getApi.execute();
      const footerConfig = res?.data?.footerConfig ?? null;
      const mapped = mapApiToLocal(footerConfig);
      setConfig(mapped);
      setLastSaved(mapped);
      setIsDirty(false);
      return footerConfig;
    } catch (err) {
      if (err.response?.status === 404) {
        const sections = cloneDefaultSections();
        setConfig({
          ...defaultConfig,
          navigationSections: sections,
        });
        setLastSaved(null);
        return null;
      }
      throw err;
    }
  }, [getApi.execute]);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = useCallback((key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  }, []);

  const setNavGroups = useCallback((updaterOrValue) => {
    setConfig((prev) => ({
      ...prev,
      navigationSections:
        typeof updaterOrValue === 'function'
          ? updaterOrValue(prev.navigationSections)
          : updaterOrValue,
    }));
    setIsDirty(true);
  }, []);

  const setPaymentIcons = useCallback((updaterOrValue) => {
    setConfig((prev) => ({
      ...prev,
      paymentIcons: typeof updaterOrValue === 'function' ? updaterOrValue(prev.paymentIcons) : updaterOrValue,
    }));
    setIsDirty(true);
  }, []);

  const save = useCallback(async () => {
    const payload = buildUpdatePayload(config);
    const res = await updateApi.execute(payload);
    const updated = res?.data?.footerConfig ?? null;
    const mapped = mapApiToLocal(updated);
    setConfig(mapped);
    setLastSaved(mapped);
    setIsDirty(false);
    return updated;
  }, [config, updateApi.execute]);

  const reset = useCallback(() => {
    if (lastSaved) {
      setConfig({ ...lastSaved });
      setIsDirty(false);
    } else {
      load();
    }
  }, [lastSaved, load]);

  return {
    config,
    loading: getApi.loading,
    saving: updateApi.loading,
    error: getApi.error || updateApi.error,
    isDirty,
    updateField,
    setNavGroups,
    setPaymentIcons,
    load,
    save,
    reset,
  };
}
